import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import {
  UI_BUILD_HASH_PROPERTY,
  UI_BUILD_TAG_PROPERTY,
  UI_BUILD_TIME_PROPERTY,
} from '@erp/shared-utils';
import config from 'src/config';
import {
  getPosthogReplayUrl,
  getPosthogSessionId,
} from 'src/api/posthogHeaders';
import { isNetworkError } from './is-network-error';
import { isViewTransitionSkipError } from './is-view-transition-skip-error';

/**
 * Identify the current user on Sentry events. Without this, Sentry falls back
 * to the client IP address (via sendDefaultPii) as the "user", which is why
 * errors were showing an IP instead of a real user.
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  name?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

/** Clear the Sentry user on logout so subsequent events aren't misattributed. */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Flag the session as a Gently admin imitating another user. The Sentry user
 * stays the REAL admin (they're the one driving the session and the one to ask
 * about an error); this tag is what says the screen belonged to someone else.
 * Passing `false` clears the tag rather than stamping every ordinary event.
 */
export function setSentryImitating(imitating: boolean) {
  Sentry.setTag('imitating', imitating || undefined);
}

/**
 * Attach the current PostHog session id (tag) and a deep link to the session
 * replay (context) so Sentry errors can be traced to a replay. Read at capture
 * time so the values reflect the session the error actually occurred in.
 */
function attachPosthogReplay(event: ErrorEvent): void {
  const sessionId = getPosthogSessionId();
  if (sessionId) {
    event.tags = { ...event.tags, posthog_session_id: sessionId };
  }

  const replayUrl = getPosthogReplayUrl();
  if (replayUrl) {
    event.contexts = {
      ...event.contexts,
      posthog: { ...event.contexts?.posthog, replay_url: replayUrl },
    };
  }
}

export const ErrorCategory = {
  UserNetworkError: 'UserNetworkError',
  Uncategorized: 'Uncategorized',
} as const;

export type ErrorCategory = (typeof ErrorCategory)[keyof typeof ErrorCategory];

export function categorizeError(event: ErrorEvent): ErrorCategory {
  const message = event.exception?.values?.[0]?.value ?? event.message ?? '';

  if (isNetworkError(message)) {
    return ErrorCategory.UserNetworkError;
  }

  return ErrorCategory.Uncategorized;
}

const EXTENSION_URL_PATTERNS = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
  /^safari-web-extension:\/\//,
];

export const initSentry = () => {
  if (!config.sentryDsn) return;

  Sentry.init({
    dsn: config.sentryDsn,
    release: config.uiBuildTag || config.uiBuildHash || undefined,
    integrations: [Sentry.featureFlagsIntegration()],
    sendDefaultPii: true,
    // Catches the common case where the top frame is an extension URL.
    // beforeSend below handles frames deeper in the stack (Sentry v9 denyUrls
    // only checks the top frame).
    denyUrls: EXTENSION_URL_PATTERNS,
    beforeSend(event) {
      const message =
        event.exception?.values?.[0]?.value ?? event.message ?? '';
      if (isViewTransitionSkipError(message)) {
        return null;
      }

      const hasExtensionFrame = event.exception?.values?.some((value) =>
        value.stacktrace?.frames?.some(
          (frame) =>
            typeof frame.filename === 'string' &&
            EXTENSION_URL_PATTERNS.some((pattern) =>
              pattern.test(frame.filename as string),
            ),
        ),
      );
      if (hasExtensionFrame) return null;

      attachPosthogReplay(event);

      const category = categorizeError(event);

      switch (category) {
        case ErrorCategory.UserNetworkError:
          event.level = 'info';
          event.tags = { ...event.tags, error_category: 'user_network_error' };
          event.fingerprint = ['user-network-error'];
          return event;
        default:
          return event;
      }
    },
  });

  Sentry.setTags({
    [UI_BUILD_HASH_PROPERTY]: config.uiBuildHash,
    [UI_BUILD_TIME_PROPERTY]: config.uiBuildTime,
    ...(config.uiBuildTag && {
      [UI_BUILD_TAG_PROPERTY]: config.uiBuildTag,
    }),
  });
};
